/**
 * THE ONE DOOR TO AN ASSIGNED WORKOUT — his approval of 2026-09-06 (*"The coach-table read RPC yes
 * lets do it"*). Migration `20260906120000` is a `security definer` function that checks visibility
 * ONCE (the union of the two SELECT policies on `coach_workouts`, word for word) and returns the whole
 * tree — sections → exercises → sets, each ordered by position — as one JSON document in the PostgREST
 * embed shape `flattenPlan` already reads, plus the exercise's `coach_exercises: {name}` object.
 * Before it the same tree paid per-row policy evaluations: 160–650 ms per workout as the client
 * (MEASURED 2026-09-02); the door answers in ~2 ms (rehearsed 2026-09-06).
 *
 * The function's NAME and ARGUMENT are a vocabulary shared with the SQL and the phone's
 * `AssignedWorkoutTree.swift`; the harness gate `beta-the-coach-tree-through-one-door` compares all
 * three, so a rename in one place goes red rather than silent.
 *
 * `rpc` is injected so this module is executed by its test without a network; the page hands it
 * `supabase.rpc`.
 */
export const assignedWorkoutTreeRpc = "assigned_workout_tree" as const;
export const assignedWorkoutTreeArgument = "p_workout_id" as const;

export type TreeSet = {
  id: string;
  position: number | null;
  reps: number | null;
  weight_kg: number | string | null;
  time_seconds: number | null;
  rounds: number | null;
  rest_seconds: number | null;
  set_type: string | null;
  /** W136 B2 — metres, `numeric` (a number or a string on the wire); absent from a door older than 20261005100000. */
  distance_m?: number | string | null;
};
export type TreeExercise = {
  id: string;
  exercise_id: string | null;
  position: number | null;
  each_side: boolean | null;
  /** The BALANCE clock (his yes 2026-10-07) — a hold counted UP until lost, no target; absent from a door older than 20261008200000. */
  hold_until_lost?: boolean | null;
  /** F15 4.1b — a row a rule tab PAUSED; the door emits it to the owning coach only (the client's door leaves the row out). */
  hidden?: boolean | null;
  tempo: string | null;
  notes: string | null;
  superset_with_next: boolean | null;
  coach_exercises: { name: string | null } | null;
  coach_workout_sets: TreeSet[] | null;
};
export type TreeSection = {
  id: string;
  position: number | null;
  name: string | null;
  format: string | null;
  section_type: string | null;
  format_config: Record<string, unknown> | null;
  description: string | null;
  /* 🔴 §6.5's LOOSE RUN, AND THE DOOR HAS CARRIED IT SINCE `20260919061000` WHILE THIS TYPE DID
     NOT. An implicit section is the one the builder makes when a coach drops a row into the gap
     between two sections: it draws NO bar and NO name on either platform, and the flag is what
     says so — never the empty name, which is the name-shaped convention §6.5 refuses by name.
     ⚠️ NULLABLE, because a tree read before that migration has no key at all, and `false` would
     be this reader inventing an answer. Every consumer asks `=== true`. */
  is_implicit: boolean | null;
  coach_workout_exercises: TreeExercise[] | null;
};
export type AssignedWorkoutTree = {
  id: string;
  name: string;
  description: string | null;
  /** The Week's batched read carries it (the editor's save token); the door (`assigned_workout_tree`) does not. */
  updated_at?: string | null;
  coach_workout_sections: TreeSection[] | null;
};

export type RpcResult = { data: unknown; error: { message: string } | null };
// Typed to the ONE function and its ONE argument, so the caller's `supabase.rpc(fn, args)` is
// checked against the generated `database.types.ts` end to end (the two `as never` casts that
// bridged the pre-push gap are gone, 2026-09-06).
export type Rpc = (
  fn: typeof assignedWorkoutTreeRpc,
  args: { [K in typeof assignedWorkoutTreeArgument]: string },
) => PromiseLike<RpcResult>;

/** A refused read (42501 on the server) arrives as `error`; an answer that is not a tree is said so. */
export async function fetchAssignedWorkoutTree(
  rpc: Rpc,
  workoutId: string,
): Promise<{ tree: AssignedWorkoutTree; error: null } | { tree: null; error: string }> {
  const r = await rpc(assignedWorkoutTreeRpc, { [assignedWorkoutTreeArgument]: workoutId });
  if (r.error) return { tree: null, error: r.error.message };
  const tree = r.data as AssignedWorkoutTree | null;
  if (tree === null || typeof tree !== "object" || typeof tree.id !== "string") {
    return { tree: null, error: "The workout came back empty." };
  }
  return { tree, error: null };
}

/** The sections in the shape `flattenPlan` reads — `[]` when the tree carries none. */
export function treeSections(tree: AssignedWorkoutTree): TreeSection[] {
  return tree.coach_workout_sections ?? [];
}

/* ── Fable fix (c) on W107 (2026-09-30): MANY TREES IN ONE READ ────────────────────────────────────────────────────────
 *
 * 🔴 RESTORED BY W137 (2026-10-02), VERBATIM. W115 removed it with the cards' lists ("the cards fetch nothing"); William's
 * EXTENDED week draws every visible day's workouts again, so the ONE batched read per visible range comes back — the
 * square (Minimized) cards still read nothing.
 *
 * The Week tab drew every card's list through the door above — one RPC per workout, ~20 at four weeks. The coach reads
 * the visible days' workouts in ONE select instead (the review queue's `PLAN_TREE_SELECT` precedent: the coach's own
 * copies through PostgREST embeds), chunked only when the id list is long. The TREE IS THE DOOR'S SHAPE, column for
 * column, so every reader downstream (`workoutOverview`, `dayExerciseLines`) is unchanged.
 *
 * ⚠️ AN EMBED ARRIVES UNORDERED (the codex; the door's SQL orders, PostgREST does not) — so the sort is HERE, where the
 * test drives a shuffled answer, and never trusted to the wire. */
/* Week editor stage 1 (2026-10-19) — `updated_at` rides along: it is `write_week_trees`' token (a copy saved elsewhere since
   this read is refused, never overwritten). */
export const WORKOUT_TREE_SELECT =
  "id, name, description, updated_at, coach_workout_sections(id, position, name, format, section_type, format_config, description, is_implicit, " +
  "coach_workout_exercises(id, exercise_id, position, each_side, hold_until_lost, tempo, notes, superset_with_next, coach_exercises(name), " +
  "coach_workout_sets(id, position, reps, weight_kg, time_seconds, rounds, rest_seconds, set_type, distance_m)))";

/** Ids per request — a PostgREST `in.(…)` rides in the URL, so a long list is split rather than sent as one huge line. */
export const TREE_READ_CHUNK = 100;

type TreeRead = (ids: string[]) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

function byPos<T extends { id: string; position: number | null }>(rows: readonly T[] | null): T[] {
  return [...(rows ?? [])].sort((a, b) =>
    (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The tree with sections, exercises and sets in position order — the door's order, restored. */
function ordered(tree: AssignedWorkoutTree): AssignedWorkoutTree {
  return {
    ...tree,
    coach_workout_sections: byPos(tree.coach_workout_sections).map((s) => ({
      ...s,
      coach_workout_exercises: byPos(s.coach_workout_exercises).map((e) => ({
        ...e, coach_workout_sets: byPos(e.coach_workout_sets),
      })),
    })),
  };
}

/** Every workout in `ids` (duplicates asked once), keyed by id. A refused chunk is the error; a missing id is absent. */
export async function fetchWorkoutTrees(
  read: TreeRead,
  ids: readonly string[],
): Promise<{ trees: Map<string, AssignedWorkoutTree>; error: string | null }> {
  const unique = [...new Set(ids)];
  const trees = new Map<string, AssignedWorkoutTree>();
  for (let i = 0; i < unique.length; i += TREE_READ_CHUNK) {
    const r = await read(unique.slice(i, i + TREE_READ_CHUNK));
    if (r.error) return { trees, error: r.error.message };
    for (const t of (Array.isArray(r.data) ? r.data : []) as AssignedWorkoutTree[]) {
      if (t !== null && typeof t === "object" && typeof t.id === "string") trees.set(t.id, ordered(t));
    }
  }
  return { trees, error: null };
}
